import { formatMoney } from '@theme/money-formatting';

const MAX_LOGO_BYTES = 10 * 1024 * 1024;
const RASTER_TYPES = new Set(['png', 'jpg', 'jpeg', 'svg']);
const ALLOWED_TYPES = new Set(['png', 'jpg', 'jpeg', 'svg', 'pdf', 'ai', 'eps']);
const TEXT_PATTERN = /^[\p{L}\p{M}\p{N} &'’".,!?#@/()+:-]*$/u;
const BURN_RGB = [44, 22, 8];
const PART_LABELS = { text: 'Text', logo: 'Logo', both: 'Text + logo' };

/**
 * Engraving configurator. Adds engraving text and/or a logo to the board line item as properties,
 * renders a burned-in preview on the board photo, attaches that preview to the order, and nests the
 * engraving service under the board through the product form's `formdata` event.
 */
class EngravingConfigurator extends HTMLElement {
  #abort = new AbortController();
  /** @type {HTMLFormElement | null} */
  #form = null;
  /** @type {HTMLImageElement | null} */
  #board = null;
  #boardLoading = false;
  /** @type {HTMLCanvasElement | null} */
  #logoInk = null;
  #logoIsVector = false;
  #renderFrame = 0;
  #exportTimer = 0;
  #exportToken = 0;
  #invalidFocused = false;
  #copy = {};
  #moneyFormat = '${{amount}}';

  connectedCallback() {
    this.#form = /** @type {HTMLFormElement | null} */ (document.getElementById(this.dataset.formId || ''));
    if (!this.#form) return;

    const { signal } = this.#abort;
    this.#copy = this.#readCopy();
    this.#moneyFormat = this.querySelector('[data-money-format]')?.innerHTML.trim() || this.#moneyFormat;

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('invalid', this.#onInvalid, { signal, capture: true });
    this.#form.addEventListener('formdata', this.#onFormData, { signal });
    this.#form.addEventListener('input', this.#onFormQuantity, { signal });
    this.#form.addEventListener('change', this.#onFormQuantity, { signal });
    document.addEventListener('quantity-selector:update', this.#onFormQuantity, { signal });
    this.querySelector('[data-remove]')?.addEventListener('click', this.#onRemoveLogo, { signal });

    const drop = this.querySelector('[data-drop]');
    if (drop) {
      for (const type of ['dragenter', 'dragover']) {
        drop.addEventListener(type, this.#onDragOver, { signal });
      }
      for (const type of ['dragleave', 'dragend']) {
        drop.addEventListener(type, () => drop.removeAttribute('data-dragging'), { signal });
      }
      drop.addEventListener('drop', this.#onDrop, { signal });
    }

    this.#sync();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();
  }

  get #parts() {
    const text = this.#toggle('text')?.checked ?? false;
    const logo = this.#toggle('logo')?.checked ?? false;
    return { text, logo, active: text || logo };
  }

  get #partKey() {
    const { text, logo } = this.#parts;
    if (text && logo) return 'both';
    if (logo) return 'logo';
    if (text) return 'text';
    return '';
  }

  get #unitsPerBoard() {
    const { text, logo, active } = this.#parts;
    if (!active) return 0;
    if (this.dataset.pricing === 'flat') return 1;
    return Number(text) + Number(logo);
  }

  get #boardQuantity() {
    const input = /** @type {HTMLInputElement | null} */ (this.#form?.querySelector('[name="quantity"]'));
    return Math.max(1, Number(input?.value) || 1);
  }

  get #textValue() {
    return this.#textInput?.value.replace(/\s+/g, ' ').trim() ?? '';
  }

  get #textInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('[data-text-input]'));
  }

  get #logoInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('[data-logo-input]'));
  }

  /** @param {'text' | 'logo'} part */
  #toggle(part) {
    return /** @type {HTMLInputElement | null} */ (this.querySelector(`[data-toggle="${part}"]`));
  }

  #readCopy() {
    try {
      const copy = JSON.parse(this.querySelector('[data-copy]')?.innerHTML || '{}');
      const decoder = document.createElement('textarea');
      for (const [key, value] of Object.entries(copy)) {
        decoder.innerHTML = String(value);
        copy[key] = decoder.value;
      }
      return copy;
    } catch {
      return {};
    }
  }

  /** @param {string} key @param {Record<string, string | number>} [vars] */
  #t(key, vars = {}) {
    let value = String(this.#copy[key] ?? '');
    for (const [name, replacement] of Object.entries(vars)) {
      value = value.replaceAll(`[${name}]`, String(replacement));
    }
    return value;
  }

  /** @param {number} cents */
  #money(cents) {
    const formatted = formatMoney(cents, this.#moneyFormat, this.dataset.currency || 'USD');
    return cents % 100 === 0 ? formatted.replace(/[.,]00(?!\d)/, '') : formatted;
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('[data-toggle]')) {
      this.#sync();
      if (target.checked) {
        const field = this.querySelector(`[data-field="${target.dataset.toggle}"]`);
        const focusable = field?.querySelector('input[type="text"]');
        if (focusable instanceof HTMLInputElement) requestAnimationFrame(() => focusable.focus({ preventScroll: true }));
      }
      return;
    }

    if (target.matches('[data-logo-input]')) {
      this.#handleLogoFile(target.files?.[0] ?? null);
    }
  };

  /** @param {Event} event */
  #onInput = (event) => {
    if (event.target !== this.#textInput) return;
    this.#validateText(false);
    this.#updateCount();
    this.#scheduleRender();
  };

  /** @param {Event} event */
  #onFormQuantity = (event) => {
    const target = event.target;
    if (event.type !== 'quantity-selector:update' && !(target instanceof HTMLInputElement && target.name === 'quantity')) {
      return;
    }
    this.#updateSummary();
  };

  /** @param {Event} event */
  #onInvalid = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    event.preventDefault();

    if (target === this.#textInput) this.#validateText(true);
    if (target === this.#logoInput) this.#showLogoError(this.#t('logo_error_empty'));

    if (!this.#invalidFocused) {
      this.#invalidFocused = true;
      const focusTarget = target === this.#logoInput ? this.querySelector('[data-drop]') : target;
      focusTarget?.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      if (target === this.#textInput) target.focus({ preventScroll: true });
      setTimeout(() => {
        this.#invalidFocused = false;
      }, 400);
    }
  };

  /** @param {FormDataEvent} event */
  #onFormData = (event) => {
    const { active } = this.#parts;
    if (!active) return;

    const data = event.formData;
    const parentId = data.get('id');
    if (!parentId) return;

    if (this.#parts.text) data.set('properties[Engraving text]', this.#textValue);

    const unitsPerBoard = this.#unitsPerBoard;
    const quantity = Math.max(1, Number(data.get('quantity')) || 1);

    data.append('items[0][id]', this.dataset.serviceVariant || '');
    data.append('items[0][quantity]', String(unitsPerBoard * quantity));
    data.append('items[0][parent_id]', String(parentId));
    data.append('items[0][properties][Includes]', PART_LABELS[this.#partKey] || '');
    data.append('items[0][properties][_per_board]', String(unitsPerBoard));
  };

  /** @param {DragEvent} event */
  #onDragOver = (event) => {
    if (!this.#parts.logo) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    this.querySelector('[data-drop]')?.setAttribute('data-dragging', '');
  };

  /** @param {DragEvent} event */
  #onDrop = (event) => {
    event.preventDefault();
    const drop = this.querySelector('[data-drop]');
    drop?.removeAttribute('data-dragging');
    const input = this.#logoInput;
    const files = event.dataTransfer?.files;
    if (!input || !files?.length || input.disabled) return;
    input.files = files;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  #onRemoveLogo = () => {
    const input = this.#logoInput;
    if (!input) return;
    input.value = '';
    this.#handleLogoFile(null);
    this.querySelector('[data-drop]')?.focus();
  };

  #sync() {
    const { text, logo, active } = this.#parts;
    const panel = /** @type {HTMLElement | null} */ (this.querySelector('[data-panel]'));

    this.toggleAttribute('data-open', active);
    if (panel) panel.inert = !active;

    this.#setFieldState('text', text);
    this.#setFieldState('logo', logo);

    const summaryInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-summary-input]'));
    if (summaryInput) {
      summaryInput.disabled = !active;
      summaryInput.value = PART_LABELS[this.#partKey] || '';
    }

    this.#form?.closest('product-form-component')?.toggleAttribute('data-engraving', active);

    if (!active) this.#clearPreviewFile();
    if (active) this.#ensureBoard();

    this.#updateCount();
    this.#updateSummary();
    this.#scheduleRender();
  }

  /** @param {'text' | 'logo'} part @param {boolean} enabled */
  #setFieldState(part, enabled) {
    const field = /** @type {HTMLElement | null} */ (this.querySelector(`[data-field="${part}"]`));
    if (field) field.hidden = !enabled;

    const input = part === 'text' ? this.#textInput : this.#logoInput;
    if (input) input.disabled = !enabled;

    if (!enabled) this.#clearError(part);
  }

  #updateCount() {
    const input = this.#textInput;
    const counter = this.querySelector('[data-count]');
    const live = this.querySelector('[data-count-live]');
    if (!input || !counter) return;

    const max = Number(this.dataset.maxChars) || 20;
    const count = Array.from(input.value).length;
    counter.textContent = `${count}/${max}`;
    counter.toggleAttribute('data-near', count >= max - 3);
    if (live) live.textContent = count >= max - 3 ? this.#t('count_label', { count, max }) : '';
  }

  #updateSummary() {
    const label = this.querySelector('[data-summary-label]');
    const price = this.querySelector('[data-summary-price]');
    const key = this.#partKey;
    if (!label || !price) return;

    if (!key) {
      label.textContent = '';
      price.textContent = '';
      return;
    }

    const quantity = this.#boardQuantity;
    const fee = this.#unitsPerBoard * (Number(this.dataset.unitPrice) || 0) * quantity;
    const summaryKey = key === 'both' ? 'summary_both' : key === 'logo' ? 'summary_logo' : 'summary_text';
    label.textContent = quantity > 1 ? `${this.#t(summaryKey)} × ${quantity}` : this.#t(summaryKey);
    price.textContent = `+${this.#money(fee)}`;
  }

  /** @param {boolean} reportEmpty */
  #validateText(reportEmpty) {
    const input = this.#textInput;
    if (!input || input.disabled) return true;

    const value = input.value;
    const badCharacters = !TEXT_PATTERN.test(value);
    const empty = !value.trim();
    const validity = badCharacters ? this.#t('text_error_chars') : empty ? this.#t('text_error_empty') : '';
    const message = badCharacters || reportEmpty ? validity : '';

    input.setCustomValidity(validity);

    if (message) this.#showError('text', message);
    else this.#clearError('text');

    input.setAttribute('aria-invalid', message ? 'true' : 'false');
    return !message;
  }

  /** @param {'text' | 'logo'} part @param {string} message */
  #showError(part, message) {
    const error = /** @type {HTMLElement | null} */ (this.querySelector(`[data-error="${part}"]`));
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  }

  /** @param {'text' | 'logo'} part */
  #clearError(part) {
    const error = /** @type {HTMLElement | null} */ (this.querySelector(`[data-error="${part}"]`));
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
    if (part === 'text') this.#textInput?.setAttribute('aria-invalid', 'false');
    if (part === 'logo') this.querySelector('[data-drop]')?.removeAttribute('data-invalid');
  }

  /** @param {string} message */
  #showLogoError(message) {
    this.#showError('logo', message);
    this.querySelector('[data-drop]')?.setAttribute('data-invalid', '');
  }

  /** @param {File | null} file */
  async #handleLogoFile(file) {
    const input = this.#logoInput;
    const drop = this.querySelector('[data-drop]');
    const empty = /** @type {HTMLElement | null} */ (this.querySelector('[data-drop-empty]'));
    const filled = /** @type {HTMLElement | null} */ (this.querySelector('[data-drop-filled]'));
    const remove = /** @type {HTMLElement | null} */ (this.querySelector('[data-remove]'));
    const thumb = /** @type {HTMLElement | null} */ (this.querySelector('[data-thumb]'));

    const reset = () => {
      if (empty) empty.hidden = false;
      if (filled) filled.hidden = true;
      if (remove) remove.hidden = true;
      drop?.removeAttribute('data-filled');
      if (thumb) {
        thumb.style.backgroundImage = '';
        thumb.textContent = '';
      }
      this.#logoInk = null;
      this.#logoIsVector = false;
      this.#scheduleRender();
    };

    if (!file) {
      reset();
      return;
    }

    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    if (!ALLOWED_TYPES.has(extension)) {
      if (input) input.value = '';
      reset();
      this.#showLogoError(this.#t('logo_error_type'));
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      if (input) input.value = '';
      reset();
      this.#showLogoError(this.#t('logo_error_size'));
      return;
    }

    this.#clearError('logo');
    if (empty) empty.hidden = true;
    if (filled) filled.hidden = false;
    if (remove) remove.hidden = false;
    drop?.setAttribute('data-filled', '');

    const name = this.querySelector('[data-file-name]');
    const size = this.querySelector('[data-file-size]');
    if (name) name.textContent = file.name;
    if (size) size.textContent = `${extension.toUpperCase()} · ${this.#formatBytes(file.size)}`;

    if (RASTER_TYPES.has(extension)) {
      const url = URL.createObjectURL(file);
      if (thumb) {
        thumb.textContent = '';
        thumb.style.backgroundImage = `url("${url}")`;
      }
      try {
        const image = await this.#loadImage(url);
        this.#logoInk = this.#toInk(image);
        this.#logoIsVector = false;
      } catch {
        this.#logoInk = null;
        this.#logoIsVector = true;
      }
    } else {
      if (thumb) {
        thumb.style.backgroundImage = '';
        thumb.textContent = extension.toUpperCase();
      }
      this.#logoInk = null;
      this.#logoIsVector = true;
    }

    this.#scheduleRender();
  }

  /** @param {number} bytes */
  #formatBytes(bytes) {
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /** @param {string} src */
  #loadImage(src) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.decoding = 'async';
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = src;
    });
  }

  #ensureBoard() {
    if (this.#board || this.#boardLoading || !this.dataset.previewSrc) return;
    this.#boardLoading = true;
    this.#loadImage(this.dataset.previewSrc)
      .then((image) => {
        this.#board = /** @type {HTMLImageElement} */ (image);
        this.#scheduleRender();
      })
      .catch(() => {})
      .finally(() => {
        this.#boardLoading = false;
      });
  }

  /**
   * Converts artwork into laser "ink": dark pixels become burn color, light pixels become transparent.
   * @param {HTMLImageElement} image
   */
  #toInk(image) {
    const longest = Math.max(image.naturalWidth || 400, image.naturalHeight || 400);
    const scale = Math.min(1, 600 / longest);
    const width = Math.max(1, Math.round((image.naturalWidth || 400) * scale));
    const height = Math.max(1, Math.round((image.naturalHeight || 400) * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(image, 0, 0, width, height);

    const pixels = ctx.getImageData(0, 0, width, height);
    const { data } = pixels;
    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;

    for (let i = 0; i < data.length; i += 4) {
      const luminance = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      const coverage = (data[i + 3] / 255) * (1 - luminance / 255);
      const ink = Math.min(1, Math.max(0, (coverage - 0.14) * 1.5));
      data[i] = BURN_RGB[0];
      data[i + 1] = BURN_RGB[1];
      data[i + 2] = BURN_RGB[2];
      data[i + 3] = Math.round(ink * 235);

      if (ink > 0.05) {
        const pixel = i / 4;
        const x = pixel % width;
        const y = Math.floor(pixel / width);
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }

    ctx.putImageData(pixels, 0, 0);
    if (maxX <= minX || maxY <= minY) return canvas;

    const trimmed = document.createElement('canvas');
    trimmed.width = maxX - minX + 1;
    trimmed.height = maxY - minY + 1;
    trimmed.getContext('2d')?.drawImage(canvas, minX, minY, trimmed.width, trimmed.height, 0, 0, trimmed.width, trimmed.height);
    return trimmed;
  }

  #updateLogoNote() {
    const note = /** @type {HTMLElement | null} */ (this.querySelector('[data-logo-note]'));
    if (note) note.hidden = !(this.#parts.logo && this.#logoIsVector);
  }

  #scheduleRender() {
    this.#updateLogoNote();
    cancelAnimationFrame(this.#renderFrame);
    this.#renderFrame = requestAnimationFrame(() => {
      this.#render();
    });
  }

  #fontFamily() {
    if (this.dataset.font === 'block') {
      const family = getComputedStyle(document.documentElement).getPropertyValue('--font-heading--family').trim();
      return { family: family || 'sans-serif', weight: '400', capHeight: 0.7, uppercase: true };
    }
    return { family: '"Times New Roman", Times, Georgia, serif', weight: '400', capHeight: 0.66, uppercase: false };
  }

  async #render() {
    const canvas = /** @type {HTMLCanvasElement | null} */ (this.querySelector('[data-canvas]'));
    const board = this.#board;
    const { text, logo, active } = this.#parts;
    if (!canvas || !board || !active) return;

    const font = this.#fontFamily();
    if (document.fonts?.load) {
      try {
        await document.fonts.load(`${font.weight} 40px ${font.family}`);
      } catch {}
    }

    const iw = board.naturalWidth;
    const ih = board.naturalHeight;
    const pct = (key, fallback) => (Number(this.dataset[key]) || fallback) / 100;

    const crop = {
      x: iw * pct('cropLeft', 11),
      y: 0,
      w: iw * (1 - pct('cropLeft', 11)),
      h: ih * pct('cropHeight', 33),
    };

    const width = 1200;
    const scale = width / crop.w;
    const height = Math.round(crop.h * scale);
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
      const stage = /** @type {HTMLElement | null} */ (canvas.parentElement);
      stage?.style.setProperty('aspect-ratio', `${width} / ${height}`);
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(board, crop.x, crop.y, crop.w, crop.h, 0, 0, width, height);

    const zoneLeft = (iw * pct('zoneLeft', 44) - crop.x) * scale;
    const zoneWidth = iw * pct('zoneWidth', 50) * scale;
    const centerY = (ih * pct('zoneTop', 13) - crop.y) * scale;
    const capHeight = ih * pct('zoneHeight', 3) * scale;

    const rawText = text ? this.#textValue : '';
    const sample = !rawText && text;
    const displayText = rawText || (text ? this.#t('sample_text') : '');
    const renderedText = font.uppercase ? displayText.toUpperCase() : displayText;

    let fontSize = capHeight / font.capHeight;
    const gap = capHeight * 0.9;

    const logoHeight = logo ? capHeight * 2.3 : 0;
    let logoWidth = 0;
    if (logo) {
      if (this.#logoInk) {
        logoWidth = Math.min(logoHeight * (this.#logoInk.width / this.#logoInk.height), capHeight * 6);
      } else {
        logoWidth = logoHeight * 1.5;
      }
    }

    ctx.font = `${font.weight} ${fontSize}px ${font.family}`;
    let textWidth = renderedText ? ctx.measureText(renderedText).width : 0;
    const available = zoneWidth - (logo ? logoWidth + (renderedText ? gap : 0) : 0);
    if (textWidth > available && textWidth > 0) {
      fontSize = Math.max(fontSize * (available / textWidth), fontSize * 0.45);
      ctx.font = `${font.weight} ${fontSize}px ${font.family}`;
      textWidth = ctx.measureText(renderedText).width;
    }

    let x = zoneLeft;

    ctx.save();
    ctx.globalCompositeOperation = 'multiply';

    if (logo) {
      const top = centerY - logoHeight / 2;
      if (this.#logoInk) {
        const drawHeight = logoWidth / (this.#logoInk.width / this.#logoInk.height);
        ctx.globalAlpha = 0.95;
        ctx.drawImage(this.#logoInk, x, centerY - drawHeight / 2, logoWidth, drawHeight);
        ctx.globalAlpha = 1;
      } else {
        this.#drawLogoPlaceholder(ctx, x, top, logoWidth, logoHeight, font);
      }
      x += logoWidth + (renderedText ? gap : 0);
    }

    if (renderedText) {
      ctx.textBaseline = 'alphabetic';
      const baseline = centerY + (fontSize * font.capHeight) / 2;
      ctx.fillStyle = sample ? `rgba(${BURN_RGB.join(',')}, 0.34)` : `rgba(${BURN_RGB.join(',')}, 0.9)`;
      ctx.fillText(renderedText, x, baseline);
      if (!sample) {
        ctx.fillStyle = 'rgba(18, 8, 2, 0.28)';
        ctx.fillText(renderedText, x + fontSize * 0.012, baseline + fontSize * 0.012);
      }
    }

    ctx.restore();

    this.#updatePreviewLabel(canvas, rawText, logo);
    this.#scheduleExport(canvas);
  }

  /**
   * @param {CanvasRenderingContext2D} ctx
   * @param {number} x @param {number} y @param {number} w @param {number} h
   * @param {{ family: string }} font
   */
  #drawLogoPlaceholder(ctx, x, y, w, h, font) {
    ctx.save();
    ctx.strokeStyle = `rgba(${BURN_RGB.join(',')}, 0.55)`;
    ctx.lineWidth = Math.max(1.5, h * 0.04);
    ctx.setLineDash([h * 0.1, h * 0.08]);
    ctx.strokeRect(x, y, w, h);
    ctx.setLineDash([]);
    ctx.fillStyle = `rgba(${BURN_RGB.join(',')}, 0.6)`;
    ctx.font = `600 ${h * 0.26}px ${font.family}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.#t('logo_placeholder'), x + w / 2, y + h / 2);
    ctx.restore();
  }

  /** @param {HTMLCanvasElement} canvas @param {string} text @param {boolean} logo */
  #updatePreviewLabel(canvas, text, logo) {
    let label = this.#t('preview_label_empty');
    if (text && logo) label = this.#t('preview_label_both', { text });
    else if (text) label = this.#t('preview_label_text', { text });
    else if (logo) label = this.#t('preview_label_logo');
    canvas.setAttribute('aria-label', label);
  }

  /** @param {HTMLCanvasElement} canvas */
  #scheduleExport(canvas) {
    clearTimeout(this.#exportTimer);
    const token = ++this.#exportToken;
    this.#exportTimer = window.setTimeout(() => {
      const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-preview-input]'));
      if (!input || !this.#parts.active || typeof DataTransfer === 'undefined') return;
      try {
        canvas.toBlob(
          (blob) => {
            if (!blob || token !== this.#exportToken || !this.#parts.active) return;
            const transfer = new DataTransfer();
            transfer.items.add(new File([blob], 'engraving-preview.jpg', { type: 'image/jpeg' }));
            input.files = transfer.files;
            input.disabled = false;
          },
          'image/jpeg',
          0.86
        );
      } catch {
        input.disabled = true;
      }
    }, 350);
  }

  #clearPreviewFile() {
    clearTimeout(this.#exportTimer);
    this.#exportToken++;
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-preview-input]'));
    if (!input) return;
    input.value = '';
    input.disabled = true;
  }
}

if (!customElements.get('engraving-configurator')) {
  customElements.define('engraving-configurator', EngravingConfigurator);
}
